import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { createOrder, getOrder, updateOrder, getMasterData, getNextIndentNumber } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Save, ArrowLeft } from 'lucide-react';
import AddTradeItemDialog from '../components/AddTradeItemDialog';
import {
  focusMobileFieldByTestId,
  keepMobileFieldVisible,
  useMobileSequentialNavigation,
} from '../hooks/useMobileSequentialNavigation';
import { MasterDataAutocomplete } from '../components/MasterDataAutocomplete';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import useSaveShortcut from '../hooks/useSaveShortcut';

// Custom hook for media query
function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    // Initialize with current match state to avoid flash on first render
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });
  
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [matches, query]);
  
  return matches;
}

export default function OrderForm() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!orderId);
  const [activeSection, setActiveSection] = useState(null); // Track focused section
  const lastSpaceTime = useRef(0);
  
  // Media query for responsive layout - true for desktop (md and up)
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { formRef, handleKeyDown: handleMobileSequentialKeyDown } = useMobileSequentialNavigation(!isDesktop);
  
  // Saved values for autocomplete - now using master data  
  const [masterBuyers, setMasterBuyers] = useState([]);
  const [masterSuppliers, setMasterSuppliers] = useState([]);
  const [masterPrintedTradeNames, setMasterPrintedTradeNames] = useState([]);
  const [masterDyedTradeNames, setMasterDyedTradeNames] = useState([]);

  // Add Trade Item Dialog state
  const [addTradeItemDialogOpen, setAddTradeItemDialogOpen] = useState(false);
  const [addTradeItemFabricType, setAddTradeItemFabricType] = useState('Printed');
  const [pendingTradeItemFieldIndex, setPendingTradeItemFieldIndex] = useState(null); // Index of the row waiting for new trade item
  const [pendingTradeItemSection, setPendingTradeItemSection] = useState(null); // 'printed' or 'dyed'
  const tradeItemDropdownFocusRef = useRef(false); // Track if Trade Item dropdown has focus

  const { register, control, handleSubmit, watch, setValue, getValues, formState: { errors, isDirty } } = useForm({
    defaultValues: {
      order_details: {
        indent_number: '',
        order_date: new Date().toISOString().split('T')[0],
        status: 'Pending',
      },
      buyers: [{ buyer_name: '', buyer_contact: '', linked_items: '' }],
      suppliers: [{ supplier_name: '', purchase_indent_number: '', purchase_rate: '' }],
      printed_fabrics: [],  // No default rows - user must add manually
      dyed_fabrics: [],     // No default rows - user must add manually
      deliveries: [],
    },
  });

  useUnsavedChanges(isDirty);

  useSaveShortcut(() => {
    formRef.current?.requestSubmit();
  });

  const { fields: buyerFields, append: appendBuyer, remove: removeBuyer } = useFieldArray({
    control,
    name: 'buyers',
  });

  const { fields: supplierFields, append: appendSupplier, remove: removeSupplier } = useFieldArray({
    control,
    name: 'suppliers',
  });

  const { fields: printedFields, append: appendPrinted, remove: removePrinted } = useFieldArray({
    control,
    name: 'printed_fabrics',
  });

  const { fields: dyedFields, append: appendDyed, remove: removeDyed } = useFieldArray({
    control,
    name: 'dyed_fabrics',
  });

  // Refs for auto-scrolling to newly added items
  const lastAddedBuyerRef = useRef(null);
  const lastAddedSupplierRef = useRef(null);
  const lastAddedPrintedRef = useRef(null);
  const lastAddedDyedRef = useRef(null);
  const lastAddedColorRef = useRef(null);

  // Auto-scroll helper function
  const scrollToElement = useCallback((element) => {
    if (element) {
      setTimeout(() => {
        const firstInput = element.querySelector('[data-mobile-sequence="true"]')
          || element.querySelector('input, [role="combobox"]');
        if (isDesktop) element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        if (firstInput) firstInput.focus({ preventScroll: true });
        if (!isDesktop) keepMobileFieldVisible(firstInput || element);
      }, 100);
    }
  }, [isDesktop]);

  // Keyboard shortcut handlers
  const handleAddBuyer = useCallback(() => {
    appendBuyer({ buyer_name: '', buyer_contact: '', linked_items: '' });
    toast.success('Buyer row added');
    setTimeout(() => scrollToElement(lastAddedBuyerRef.current), 50);
  }, [appendBuyer, scrollToElement]);

  const handleAddSupplier = useCallback(() => {
    appendSupplier({ supplier_name: '', purchase_indent_number: '', purchase_rate: '' });
    toast.success('Supplier row added');
    setTimeout(() => scrollToElement(lastAddedSupplierRef.current), 50);
  }, [appendSupplier, scrollToElement]);

  const handleAddPrintedRow = useCallback(() => {
    const newRowIndex = printedFields.length;
    appendPrinted({ trade_name: '', design_number: '', quantity: '', si_unit: '', num_matchings: '', total_qty: 0, purchase_rate: '', sales_rate: '', purchase_cd_percent: '', sales_cd_percent: '', purchase_amount: 0, sales_amount: 0, status: 'Ready', bale_no: '' });
    toast.success('Printed fabric row added');
    if (isDesktop) {
      setTimeout(() => scrollToElement(lastAddedPrintedRef.current), 50);
    } else {
      focusMobileFieldByTestId(`design-number-mobile-${newRowIndex}`);
    }
  }, [appendPrinted, isDesktop, printedFields.length, scrollToElement]);

  const handleAddDyedFabric = useCallback(() => {
    const newRowIndex = dyedFields.length;
    // Add a dyed fabric row with mode=null (unassigned)
    // The mode will be determined when a Trade Name is selected
    appendDyed({ 
      mode: null, // Will be set based on Trade Name's dyed_ordering_mode
      trade_name: '', 
      // Fixed Chart fields
      design_number: '',
      qty_per_matching: '', 
      matching: '', 
      total_qty: 0,
      unit: 'Metre',
      purchase_rate: '', 
      purchase_amount: 0,
      sales_rate: '', 
      sales_amount: 0,
      purchase_cd_percent: '',
      sales_cd_percent: '',
      status: 'Ready', 
      bale_number: '',
      // Pick & Choose fields
      trade_purchase_rate: '',
      trade_sales_rate: '',
      trade_purchase_cd_percent: '',
      trade_sales_cd_percent: '',
      colors: []
    });
    toast.success('Dyed fabric row added - select a Trade Name to continue');
    if (isDesktop) {
      setTimeout(() => scrollToElement(lastAddedDyedRef.current), 50);
    } else {
      focusMobileFieldByTestId(`trade-name-dyed-placeholder-${newRowIndex}`);
    }
  }, [appendDyed, dyedFields.length, isDesktop, scrollToElement]);

  // Handler to open Add Trade Item dialog
  const handleOpenAddTradeItemDialog = useCallback((fabricType, fieldIndex, section) => {
    setAddTradeItemFabricType(fabricType);
    setPendingTradeItemFieldIndex(fieldIndex);
    setPendingTradeItemSection(section);
    setAddTradeItemDialogOpen(true);
  }, []);

  // Handler when a new trade item is created
  const handleTradeItemCreated = useCallback(async (newItem) => {
    // Store the pending selection info before clearing
    const section = pendingTradeItemSection;
    const fieldIndex = pendingTradeItemFieldIndex;
    
    // Clear pending state immediately
    setPendingTradeItemFieldIndex(null);
    setPendingTradeItemSection(null);
    
    // Use flushSync to force React to synchronously update state
    // This ensures the SelectItems are re-rendered before we set the value
    flushSync(() => {
      if (newItem.fabric_type === 'Printed' || newItem.fabric_type === 'Yarn Dyed') {
        setMasterPrintedTradeNames(prev => [...prev, newItem]);
      } else {
        setMasterDyedTradeNames(prev => [...prev, newItem]);
      }
    });
    
    // Auto-select the new item in the pending field
    // Now the SelectItems have been rendered with the new item
    if (section && fieldIndex !== null) {
      const fieldPath = section === 'printed' 
        ? `printed_fabrics.${fieldIndex}.trade_name`
        : `dyed_fabrics.${fieldIndex}.trade_name`;
      
      setValue(fieldPath, newItem.name);
      toast.success(`"${newItem.name}" selected`);
    }
    
    // Also refresh from backend to ensure consistency (runs in background)
    fetchSavedValues();
  }, [pendingTradeItemSection, pendingTradeItemFieldIndex, setValue]);

  // Keyboard shortcut: Double Spacebar for Add buttons, Alt+Space for Add Color, Alt+C for Add Trade Item
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in input/textarea (but allow for Trade Item dropdown)
      const tagName = e.target.tagName.toLowerCase();
      const isTyping = tagName === 'input' || tagName === 'textarea';
      
      // ALT + C: Add New Trade Item (only when Trade Item dropdown is focused or open)
      if (e.altKey && (e.key === 'c' || e.key === 'C')) {
        // Check if focus is on a Trade Item dropdown (data-testid contains 'trade-name')
        const focusedElement = document.activeElement;
        const isTradeItemDropdown = focusedElement?.closest('[data-testid*="trade-name-"]');
        // Fixed selector: compound attribute selector (no space) to match element with both attributes
        const openTradeItemTrigger = document.querySelector('[data-testid*="trade-name-"][data-state="open"]');
        // Also check if we're inside a Radix Select portal (listbox role) - this happens when dropdown is open
        const isInsideSelectPortal = focusedElement?.closest('[role="listbox"]') || focusedElement?.closest('[data-radix-select-content]');
        
        if (isTradeItemDropdown || openTradeItemTrigger || isInsideSelectPortal || tradeItemDropdownFocusRef.current) {
          e.preventDefault();
          
          // Determine if it's Printed or Dyed section
          // When dropdown is open, we need to check the trigger that's open
          let printedSection = focusedElement?.closest('[data-section="printed"]');
          let dyedSection = focusedElement?.closest('[data-section="dyed"]');
          
          // If we're in a portal, find the open trigger and determine section from there
          if (!printedSection && !dyedSection && openTradeItemTrigger) {
            printedSection = openTradeItemTrigger.closest('[data-section="printed"]');
            dyedSection = openTradeItemTrigger.closest('[data-section="dyed"]');
          }
          
          // Try to extract the index from the data-testid
          let testId = focusedElement?.getAttribute('data-testid') || 
                       focusedElement?.closest('[data-testid*="trade-name-"]')?.getAttribute('data-testid') || '';
          
          // If testId not found from focus, try the open trigger
          if (!testId && openTradeItemTrigger) {
            testId = openTradeItemTrigger.getAttribute('data-testid') || '';
          }
          
          let fieldIndex = 0;
          let section = 'printed';
          let fabricType = 'Printed';
          
          if (testId.includes('trade-name-printed-')) {
            const match = testId.match(/trade-name-printed-(?:mobile-)?(\d+)/);
            fieldIndex = match ? parseInt(match[1], 10) : 0;
            section = 'printed';
            fabricType = 'Printed';
          } else if (testId.includes('trade-name-dyed-')) {
            const match = testId.match(/trade-name-dyed-(?:mobile-)?(\d+)/);
            fieldIndex = match ? parseInt(match[1], 10) : 0;
            section = 'dyed';
            fabricType = 'Dyed';
          } else if (printedSection) {
            section = 'printed';
            fabricType = 'Printed';
          } else if (dyedSection) {
            section = 'dyed';
            fabricType = 'Dyed';
          }
          
          handleOpenAddTradeItemDialog(fabricType, fieldIndex, section);
          return;
        }
      }
      
      // + key (Plus/Equal with Shift, or NumpadAdd) for Add Color in Dyed Fabrics
      if (!isTyping && (e.key === '+' || e.code === 'NumpadAdd')) {
        e.preventDefault();
        // Find the Add Color button in the active dyed fabric and click it
        const addColorBtn = document.querySelector('[data-testid^="add-color-"]');
        if (addColorBtn) {
          addColorBtn.click();
          toast.success('Color row added (+)');
        }
        return;
      }
      
      // Double Spacebar detection (only when not typing)
      if (e.code === 'Space' && !isTyping && !e.altKey && !e.ctrlKey && !e.metaKey) {
        const now = Date.now();
        const timeDiff = now - lastSpaceTime.current;
        
        if (timeDiff < 400 && timeDiff > 50) { // Double tap detected (between 50-400ms)
          e.preventDefault();
          
          // Determine which section to add to based on activeSection or focused element
          const focusedElement = document.activeElement;
          const buyersSection = focusedElement?.closest('[data-section="buyers"]');
          const suppliersSection = focusedElement?.closest('[data-section="suppliers"]');
          const printedSection = focusedElement?.closest('[data-section="printed"]');
          const dyedSection = focusedElement?.closest('[data-section="dyed"]');
          
          if (buyersSection || activeSection === 'buyers') {
            handleAddBuyer();
          } else if (suppliersSection || activeSection === 'suppliers') {
            handleAddSupplier();
          } else if (printedSection || activeSection === 'printed') {
            handleAddPrintedRow();
          } else if (dyedSection || activeSection === 'dyed') {
            handleAddDyedFabric();
          } else {
            // Default: add to printed fabrics
            handleAddPrintedRow();
          }
        }
        lastSpaceTime.current = now;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeSection, handleAddBuyer, handleAddSupplier, handleAddPrintedRow, handleAddDyedFabric, handleOpenAddTradeItemDialog]);

  useEffect(() => {
    fetchSavedValues();
    if (orderId) {
      fetchOrder();
    } else {
      // Fetch next indent number for new orders
      fetchNextIndentNumber();
    }
  }, [orderId]);

  const fetchNextIndentNumber = async () => {
    try {
      const response = await getNextIndentNumber();
      setValue('order_details.indent_number', response.data.indent_number);
    } catch (error) {
      console.error('Failed to fetch next indent number:', error);
    }
  };

  const fetchSavedValues = async () => {
    try {
      // Fetch active trade names only for Order Entry (active_only=true)
      // Dyed fabrics section includes both 'Dyed' and 'Yarn Dyed' types
      const [buyers, suppliers, printedTrades, dyedTrades, yarnDyedTrades] = await Promise.all([
        getMasterData('buyer'),
        getMasterData('supplier'),
        getMasterData('trade_name', 'Printed', true),    // Only active trade names
        getMasterData('trade_name', 'Dyed', true),       // Only active trade names
        getMasterData('trade_name', 'Yarn Dyed', true),  // Only active trade names
      ]);
      setMasterBuyers(buyers.data);
      setMasterSuppliers(suppliers.data);
      setMasterPrintedTradeNames(printedTrades.data);
      // Combine Dyed and Yarn Dyed for the dyed fabrics section
      setMasterDyedTradeNames([...dyedTrades.data, ...yarnDyedTrades.data].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Failed to fetch master data:', error);
      toast.error('Failed to load master lists. Please contact admin to add items.');
    }
  };

  const fetchOrder = async () => {
    try {
      const response = await getOrder(orderId);
      const order = response.data;
      
      // Populate form with existing order data
      setValue('order_details', order.order_details);
      setValue('buyers', order.buyers.length > 0 ? order.buyers : [{ buyer_name: '', buyer_contact: '', linked_items: '' }]);
      setValue('suppliers', order.suppliers.length > 0 ? order.suppliers : [{ supplier_name: '', purchase_indent_number: '', purchase_rate: '' }]);
      setValue('printed_fabrics', order.printed_fabrics.length > 0 ? order.printed_fabrics : []);
      setValue('dyed_fabrics', order.dyed_fabrics.length > 0 ? order.dyed_fabrics : []);
      setValue('deliveries', order.deliveries || []);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Failed to load order');
    } finally {
      setInitialLoading(false);
    }
  };

  // Real-time auto-calculation for printed fabrics
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name && name.startsWith('printed_fabrics')) {
        const match = name.match(/printed_fabrics\.(\d+)\.(quantity|num_matchings|purchase_rate|sales_rate|trade_name)/);
        if (match) {
          const index = parseInt(match[1]);
          const fabric = value.printed_fabrics[index];
          if (fabric) {
            // Parse string inputs to numbers for calculations
            const qty = parseFloat(fabric.quantity) || 0;
            const matchings = parseFloat(fabric.num_matchings) || 0;
            const purchaseRate = parseFloat(fabric.purchase_rate) || 0;
            const salesRate = parseFloat(fabric.sales_rate) || 0;
            
            const total_qty = qty * matchings;
            const purchase_amount = total_qty * purchaseRate;
            const sales_amount = total_qty * salesRate;
            setValue(`printed_fabrics.${index}.total_qty`, total_qty, { shouldValidate: false });
            setValue(`printed_fabrics.${index}.purchase_amount`, purchase_amount, { shouldValidate: false });
            setValue(`printed_fabrics.${index}.sales_amount`, sales_amount, { shouldValidate: false });
            
            // Auto-fill SI unit from master data when trade name is selected
            if (match[2] === 'trade_name' && fabric.trade_name) {
              const selectedTrade = masterPrintedTradeNames.find(t => t.name === fabric.trade_name);
              if (selectedTrade) {
                // Convert to abbreviations: Metre -> MTRS, Kgs -> KGS
                let siUnitAbbr = selectedTrade.si_unit || '';
                if (siUnitAbbr.toLowerCase() === 'metre') siUnitAbbr = 'MTRS';
                else if (siUnitAbbr.toLowerCase() === 'kgs') siUnitAbbr = 'KGS';
                else siUnitAbbr = siUnitAbbr.toUpperCase();
                setValue(`printed_fabrics.${index}.si_unit`, siUnitAbbr, { shouldValidate: false });
              }
              // Auto-fill default Purchase CD% from supplier master (only if not already set by user)
              if (!fabric.purchase_cd_percent && selectedTrade.supplier_id) {
                const linkedSupplier = masterSuppliers.find(s => s.id === selectedTrade.supplier_id);
                if (linkedSupplier && linkedSupplier.default_purchase_cd_percent) {
                  setValue(`printed_fabrics.${index}.purchase_cd_percent`, linkedSupplier.default_purchase_cd_percent, { shouldValidate: false });
                }
              }
            }
          }
        }
      }
      
      if (name && name.startsWith('dyed_fabrics')) {
        const colorMatch = name.match(/dyed_fabrics\.(\d+)\.colors\.(\d+)\.(qty|purchase_rate|sales_rate)/);
        const tradePurchaseMatch = name.match(/dyed_fabrics\.(\d+)\.trade_purchase_rate/);
        const tradeSalesMatch = name.match(/dyed_fabrics\.(\d+)\.trade_sales_rate/);
        const tradeNameMatch = name.match(/dyed_fabrics\.(\d+)\.trade_name/);
        
        if (colorMatch) {
          const fabricIndex = parseInt(colorMatch[1]);
          const colorIndex = parseInt(colorMatch[2]);
          const color = value.dyed_fabrics[fabricIndex]?.colors[colorIndex];
          if (color) {
            // Parse string inputs to numbers for calculations
            const colorQty = parseFloat(color.qty) || 0;
            const colorPurchaseRate = parseFloat(color.purchase_rate) || 0;
            const colorSalesRate = parseFloat(color.sales_rate) || 0;
            
            const purchase_amount = colorQty * colorPurchaseRate;
            const sales_amount = colorQty * colorSalesRate;
            setValue(`dyed_fabrics.${fabricIndex}.colors.${colorIndex}.purchase_amount`, purchase_amount, { shouldValidate: false });
            setValue(`dyed_fabrics.${fabricIndex}.colors.${colorIndex}.sales_amount`, sales_amount, { shouldValidate: false });
          }
        }
        
        // When trade_purchase_rate changes, update all color purchase rates
        if (tradePurchaseMatch) {
          const fabricIndex = parseInt(tradePurchaseMatch[1]);
          const fabric = value.dyed_fabrics[fabricIndex];
          if (fabric && fabric.colors) {
            const tradePRate = parseFloat(fabric.trade_purchase_rate) || 0;
            fabric.colors.forEach((color, colorIndex) => {
              setValue(`dyed_fabrics.${fabricIndex}.colors.${colorIndex}.purchase_rate`, tradePRate, { shouldValidate: false });
              const colorQty = parseFloat(color.qty) || 0;
              const purchase_amount = colorQty * tradePRate;
              setValue(`dyed_fabrics.${fabricIndex}.colors.${colorIndex}.purchase_amount`, purchase_amount, { shouldValidate: false });
            });
          }
        }
        
        // When trade_sales_rate changes, update all color sales rates
        if (tradeSalesMatch) {
          const fabricIndex = parseInt(tradeSalesMatch[1]);
          const fabric = value.dyed_fabrics[fabricIndex];
          if (fabric && fabric.colors) {
            const tradeSRate = parseFloat(fabric.trade_sales_rate) || 0;
            fabric.colors.forEach((color, colorIndex) => {
              setValue(`dyed_fabrics.${fabricIndex}.colors.${colorIndex}.sales_rate`, tradeSRate, { shouldValidate: false });
              const colorQty = parseFloat(color.qty) || 0;
              const sales_amount = colorQty * tradeSRate;
              setValue(`dyed_fabrics.${fabricIndex}.colors.${colorIndex}.sales_amount`, sales_amount, { shouldValidate: false });
            });
          }
        }
        
        // When trade name is selected, auto-fill unit for all colors
        if (tradeNameMatch) {
          const fabricIndex = parseInt(tradeNameMatch[1]);
          const fabric = value.dyed_fabrics[fabricIndex];
          if (fabric && fabric.trade_name) {
            const selectedTrade = masterDyedTradeNames.find(t => t.name === fabric.trade_name);
            if (selectedTrade && fabric.colors) {
              // Convert to abbreviations: Metre -> MTRS, Kgs -> KGS
              let siUnitAbbr = selectedTrade.si_unit || 'Metre';
              if (siUnitAbbr.toLowerCase() === 'metre') siUnitAbbr = 'MTRS';
              else if (siUnitAbbr.toLowerCase() === 'kgs') siUnitAbbr = 'KGS';
              else siUnitAbbr = siUnitAbbr.toUpperCase();
              fabric.colors.forEach((color, colorIndex) => {
                setValue(`dyed_fabrics.${fabricIndex}.colors.${colorIndex}.unit`, siUnitAbbr, { shouldValidate: false });
              });
            }
          }
        }
      }
    });
    
    return () => subscription.unsubscribe();
  }, [watch, setValue, masterPrintedTradeNames, masterDyedTradeNames, masterSuppliers]);

  const onSubmit = async (data) => {
    // Filter valid printed fabrics: Trade Name + Design No + Qty > 0
    // Also convert string values to numbers for backend
    const validPrintedFabrics = (data.printed_fabrics || [])
      .filter(item => item.trade_name && item.design_number && parseFloat(item.quantity) > 0)
      .map(item => ({
        ...item,
        quantity: parseFloat(item.quantity) || 0,
        num_matchings: parseFloat(item.num_matchings) || 0,
        total_qty: parseFloat(item.total_qty) || 0,
        purchase_rate: parseFloat(item.purchase_rate) || 0,
        sales_rate: parseFloat(item.sales_rate) || 0,
        purchase_amount: parseFloat(item.purchase_amount) || 0,
        sales_amount: parseFloat(item.sales_amount) || 0,
        purchase_cd_percent: parseFloat(item.purchase_cd_percent) || 0,
        sales_cd_percent: parseFloat(item.sales_cd_percent) || 0,
      }));
    
    // Filter valid dyed fabrics based on mode
    // Fixed Chart: Trade Name + Qty > 0 + Matching > 0
    // Pick & Choose: Trade Name + at least one color with qty > 0
    const validDyedFabrics = (data.dyed_fabrics || []).filter(item => {
      if (!item.trade_name) return false;
      
      // Fixed Chart mode: check qty_per_matching and matching
      if (item.mode === 'fixed_chart') {
        return parseFloat(item.qty_per_matching) > 0 && parseFloat(item.matching) > 0;
      }
      
      // Pick & Choose mode: check if at least one color has valid data
      const validColors = (item.colors || []).filter(
        c => c.color_number && parseFloat(c.qty) > 0
      );
      return validColors.length > 0;
    }).map(item => {
      // Fixed Chart mode: convert numeric fields
      if (item.mode === 'fixed_chart') {
        return {
          ...item,
          qty_per_matching: parseFloat(item.qty_per_matching) || 0,
          matching: parseInt(item.matching, 10) || 0,
          total_qty: parseFloat(item.total_qty) || 0,
          purchase_rate: parseFloat(item.purchase_rate) || 0,
          sales_rate: parseFloat(item.sales_rate) || 0,
          purchase_amount: parseFloat(item.purchase_amount) || 0,
          sales_amount: parseFloat(item.sales_amount) || 0,
          purchase_cd_percent: parseFloat(item.purchase_cd_percent) || 0,
          sales_cd_percent: parseFloat(item.sales_cd_percent) || 0,
          // Ensure Pick & Choose fields are properly set for compatibility
          trade_purchase_rate: 0,
          trade_sales_rate: 0,
          trade_purchase_cd_percent: 0,
          trade_sales_cd_percent: 0,
          colors: [],
        };
      }
      
      // Pick & Choose mode: convert colors
      return {
        ...item,
        mode: item.mode || 'pick_choose',
        trade_purchase_rate: parseFloat(item.trade_purchase_rate) || 0,
        trade_sales_rate: parseFloat(item.trade_sales_rate) || 0,
        trade_purchase_cd_percent: parseFloat(item.trade_purchase_cd_percent) || 0,
        trade_sales_cd_percent: parseFloat(item.trade_sales_cd_percent) || 0,
        // Only keep valid colors and convert to numbers
        colors: (item.colors || [])
          .filter(c => c.color_number && parseFloat(c.qty) > 0)
          .map(c => ({
            ...c,
            qty: parseFloat(c.qty) || 0,
            purchase_rate: parseFloat(c.purchase_rate) || 0,
            sales_rate: parseFloat(c.sales_rate) || 0,
            purchase_amount: parseFloat(c.purchase_amount) || 0,
            sales_amount: parseFloat(c.sales_amount) || 0,
          })),
        // Ensure Fixed Chart fields are properly set for compatibility
        qty_per_matching: 0,
        matching: 0,
        total_qty: 0,
        purchase_rate: 0,
        sales_rate: 0,
        purchase_amount: 0,
        sales_amount: 0,
        purchase_cd_percent: 0,
        sales_cd_percent: 0,
        status: 'Ready',
        bale_number: '',
      };
    });
    
    // Check if there's at least one valid fabric item
    if (validPrintedFabrics.length === 0 && validDyedFabrics.length === 0) {
      toast.error('Please add at least one valid fabric item before saving order.');
      return;
    }
    
    // Prepare data with only valid items (numbers converted)
    // Also clean suppliers to convert purchase_rate to number
    const cleanedSuppliers = (data.suppliers || []).map(s => ({
      ...s,
      purchase_rate: parseFloat(s.purchase_rate) || 0,
    }));
    
    const cleanedData = {
      ...data,
      suppliers: cleanedSuppliers,
      printed_fabrics: validPrintedFabrics,
      dyed_fabrics: validDyedFabrics,
    };
    
    setLoading(true);
    try {
      if (orderId) {
        await updateOrder(orderId, cleanedData);
        toast.success('Order updated successfully!');
      } else {
        await createOrder(cleanedData);
        toast.success('Order created successfully!');
      }
      navigate('/orders');
    } catch (error) {
      console.error('Failed to save order:', error);
      toast.error(error.response?.data?.detail || 'Failed to save order');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-maroon-600">Loading order...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-5" data-testid="order-form">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold tracking-tight text-maroon-950">
            {orderId ? 'Edit Order' : 'Create New Order'}
          </h1>
          <p className="text-xs lg:text-sm text-maroon-700 mt-1">Fill order details in spreadsheet format</p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/orders')}
          className="border-maroon-300 hover:bg-maroon-50 w-full sm:w-auto"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <form ref={formRef} onKeyDownCapture={handleMobileSequentialKeyDown} onSubmit={handleSubmit(onSubmit, (errors) => {
        console.error('Form validation errors:', errors);
        toast.error('Please fix the form errors before saving.');
      })} className="space-y-4 lg:space-y-5">
        {/* Order Details Section */}
        <Card className="border-maroon-200 border-l-4 border-l-indigo-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-base lg:text-lg font-semibold text-maroon-950">Order Details</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="indent_number" className="text-xs font-medium text-maroon-700 uppercase">
                  Indent Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="indent_number"
                  data-testid="indent-number-input"
                  {...register('order_details.indent_number')}
                  className="mt-1 h-9 bg-maroon-100 font-semibold"
                  placeholder="Auto-generated"
                  readOnly={!orderId}
                />
                {errors.order_details?.indent_number && (
                  <p className="text-red-500 text-xs mt-1">Required</p>
                )}
              </div>
              <div>
                <Label htmlFor="order_date" className="text-xs font-medium text-maroon-700 uppercase">
                  Order Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="order_date"
                  data-testid="order-date-input"
                  data-mobile-sequence={!isDesktop ? 'true' : undefined}
                  type="date"
                  {...register('order_details.order_date', { required: true })}
                  className="mt-1 h-9"
                />
              </div>
              <div>
                <Label htmlFor="status" className="text-xs font-medium text-maroon-700 uppercase">
                  Status
                </Label>
                <Select
                  value={watch('order_details.status')}
                  onValueChange={(value) => setValue('order_details.status', value)}
                >
                  <SelectTrigger data-testid="status-select" className="mt-1 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Buyers and Suppliers Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Buyers Section */}
          <Card 
            className="border-maroon-200 border-l-4 border-l-emerald-500"
            data-section="buyers"
            onFocus={() => setActiveSection('buyers')}
          >
            <CardHeader className="pb-2 p-4 lg:p-3">
              <CardTitle className="text-sm lg:text-base font-semibold text-maroon-950">
                Buyers
                <span className="ml-2 text-xs font-normal text-maroon-500">(Double Space to add)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 p-4 lg:p-3 pt-0">
              <div className="space-y-2">
                {buyerFields.map((field, index) => (
                  <div 
                    key={field.id} 
                    className="flex items-center gap-2"
                    ref={index === buyerFields.length - 1 ? lastAddedBuyerRef : null}
                  >
                    <MasterDataAutocomplete
                      value={watch(`buyers.${index}.buyer_name`) || ''}
                      onValueChange={(value) => setValue(`buyers.${index}.buyer_name`, value)}
                      options={masterBuyers.map((buyer) => ({ value: buyer.name, label: buyer.name }))}
                      placeholder="Select buyer..."
                      searchPlaceholder="Search buyers..."
                      emptyMessage="No matching buyer in Master Data"
                      className="flex-1"
                      inputClassName="h-9"
                      testId={`buyer-name-${index}`}
                    />
                    <Button
                      type="button"
                      data-testid={`remove-buyer-${index}`}
                      onClick={() => removeBuyer(index)}
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 p-0 text-red-600 hover:bg-red-50 shrink-0"
                      disabled={buyerFields.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-3">
                <Button
                  type="button"
                  data-testid="add-buyer-button"
                  onClick={handleAddBuyer}
                  size="sm"
                  variant="outline"
                  className="h-8 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Buyer
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Suppliers Section */}
          <Card 
            className="border-maroon-200 border-l-4 border-l-amber-500"
            data-section="suppliers"
            onFocus={() => setActiveSection('suppliers')}
          >
            <CardHeader className="pb-2 p-4 lg:p-3">
              <CardTitle className="text-sm lg:text-base font-semibold text-maroon-950">
                Suppliers
                <span className="ml-2 text-xs font-normal text-maroon-500">(Double Space to add)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 p-4 lg:p-3 pt-0">
              <div className="space-y-2">
                {supplierFields.map((field, index) => (
                  <div 
                    key={field.id} 
                    className="flex items-center gap-2"
                    ref={index === supplierFields.length - 1 ? lastAddedSupplierRef : null}
                  >
                    <MasterDataAutocomplete
                      value={watch(`suppliers.${index}.supplier_name`) || ''}
                      onValueChange={(value) => setValue(`suppliers.${index}.supplier_name`, value)}
                      options={masterSuppliers.map((supplier) => ({ value: supplier.name, label: supplier.name }))}
                      placeholder="Select supplier..."
                      searchPlaceholder="Search suppliers..."
                      emptyMessage="No matching supplier in Master Data"
                      className="flex-1"
                      inputClassName="h-9"
                      testId={`supplier-name-${index}`}
                    />
                    <Input
                      data-testid={`purchase-indent-${index}`}
                      data-mobile-sequence={!isDesktop ? 'true' : undefined}
                      {...register(`suppliers.${index}.purchase_indent_number`)}
                      className="h-9 w-32 border-maroon-200"
                      placeholder="PI No."
                    />
                    <Button
                      type="button"
                      data-testid={`remove-supplier-${index}`}
                      onClick={() => removeSupplier(index)}
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 p-0 text-red-600 hover:bg-red-50 shrink-0"
                      disabled={supplierFields.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-3">
                <Button
                  type="button"
                  data-testid="add-supplier-button"
                  onClick={handleAddSupplier}
                  size="sm"
                  variant="outline"
                  className="h-8 border-amber-600 text-amber-700 hover:bg-amber-50"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Supplier
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Printed Fabrics Section - Responsive Table/Card Format */}
        <Card 
          className="border-maroon-200 border-l-4 border-l-rose-500"
          data-section="printed"
          onFocus={() => setActiveSection('printed')}
        >
          <CardHeader className="pb-3 p-4 lg:p-5">
            <CardTitle className="text-sm lg:text-lg font-semibold text-maroon-950">
              Printed Fabrics
              <span className="ml-2 text-xs font-normal text-maroon-500">(Double Space to add)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 p-2 lg:p-5 lg:pt-0">
            {/* Desktop Table View - Conditional rendering to avoid duplicate registrations */}
            {isDesktop && (
            <div className="border border-maroon-200 rounded-md">
              <table className="w-full border-collapse table-fixed">
                <thead>
                  <tr className="bg-maroon-50 border-b border-maroon-200">
                    <th className="text-left py-2 px-2 text-xs font-semibold text-maroon-800 uppercase w-[130px]">Trade Name</th>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-maroon-800 uppercase w-[90px]">Design No.</th>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-maroon-800 uppercase w-[70px]">Qty</th>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-maroon-800 uppercase bg-maroon-100 w-[55px]">Unit</th>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-maroon-800 uppercase w-[55px]">Match</th>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-maroon-800 uppercase bg-maroon-100 w-[70px]">Total</th>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-emerald-700 uppercase bg-emerald-50 w-[80px]">P.Rate</th>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-emerald-700 uppercase bg-emerald-50 w-[90px]">P.Amt</th>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-blue-700 uppercase bg-blue-50 w-[80px]">S.Rate</th>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-blue-700 uppercase bg-blue-50 w-[90px]">S.Amt</th>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-maroon-800 uppercase w-[90px]">Status</th>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-maroon-800 uppercase w-[70px]">Bale</th>
                    <th className="text-center py-2 px-1 text-xs font-semibold text-maroon-800 uppercase w-[40px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {printedFields.length === 0 && (
                    <tr>
                      <td colSpan="13" className="py-8 text-center">
                        <div className="text-maroon-500 text-sm">
                          No printed fabric items added yet.
                        </div>
                        <div className="text-maroon-400 text-xs mt-1">
                          Click &quot;Add Row&quot; below to add fabric items.
                        </div>
                      </td>
                    </tr>
                  )}
                  {printedFields.map((field, index) => {
                    const currentStatus = watch(`printed_fabrics.${index}.status`);
                    return (
                      <tr 
                        key={field.id} 
                        className="border-b border-maroon-100 hover:bg-maroon-50/50"
                        ref={index === printedFields.length - 1 ? lastAddedPrintedRef : null}
                      >
                        <td className="py-1 px-2">
                          <MasterDataAutocomplete
                            value={watch(`printed_fabrics.${index}.trade_name`) || ''}
                            onValueChange={(value) => {
                              if (value === '__add_new__') {
                                handleOpenAddTradeItemDialog('Printed', index, 'printed');
                              } else {
                                setValue(`printed_fabrics.${index}.trade_name`, value);
                              }
                            }}
                            options={[
                              { value: '__add_new__', label: 'Add New Trade Item', description: 'Alt+C', pinned: true, testId: `add-trade-item-printed-${index}` },
                              ...masterPrintedTradeNames.map((tradeName) => ({
                                value: tradeName.name,
                                label: tradeName.name,
                                description: tradeName.supplier_name || '',
                                keywords: tradeName.supplier_name || '',
                              })),
                            ]}
                            placeholder="Select..."
                            searchPlaceholder="Search trade items..."
                            emptyMessage="No matching printed trade item"
                            inputClassName="h-8 text-xs"
                            testId={`trade-name-printed-${index}`}
                            onFocus={() => { tradeItemDropdownFocusRef.current = true; }}
                            onBlur={() => { tradeItemDropdownFocusRef.current = false; }}
                          />
                        </td>
                        <td className="py-1 px-2">
                          <Input
                            data-testid={`design-number-${index}`}
                            {...register(`printed_fabrics.${index}.design_number`)}
                            className="h-8 border-maroon-200 text-xs"
                            placeholder="000000"
                          />
                        </td>
                        <td className="py-1 px-2">
                          <Input
                            data-testid={`quantity-${index}`}
                            type="text"
                            inputMode="decimal"
                            {...register(`printed_fabrics.${index}.quantity`)}
                            className="h-8 border-maroon-200 text-xs"
                            placeholder="Qty"
                          />
                        </td>
                        <td className="py-1 px-2 bg-maroon-100">
                          <div 
                            data-testid={`si-unit-${index}`}
                            className="h-8 flex items-center justify-center bg-maroon-200 rounded font-bold text-xs text-maroon-800"
                          >
                            {watch(`printed_fabrics.${index}.si_unit`) || '-'}
                          </div>
                        </td>
                        <td className="py-1 px-2">
                          <Input
                            data-testid={`num-matchings-${index}`}
                            type="text"
                            inputMode="numeric"
                            {...register(`printed_fabrics.${index}.num_matchings`)}
                            className="h-8 border-maroon-200 text-xs"
                            placeholder="Match"
                          />
                        </td>
                        <td className="py-1 px-2 bg-maroon-100">
                          <div 
                            data-testid={`total-qty-${index}`}
                            className="h-8 flex items-center justify-center bg-maroon-200 rounded font-bold text-xs"
                          >
                            {watch(`printed_fabrics.${index}.total_qty`) || '-'}
                          </div>
                        </td>
                        <td className="py-1 px-2 bg-emerald-50">
                          <Input
                            data-testid={`purchase-rate-printed-${index}`}
                            type="text"
                            inputMode="decimal"
                            {...register(`printed_fabrics.${index}.purchase_rate`)}
                            className="h-8 border-emerald-300 bg-emerald-50 text-emerald-800 text-xs"
                            placeholder="P.Rate"
                          />
                          <Input
                            data-testid={`purchase-cd-printed-${index}`}
                            type="text"
                            inputMode="decimal"
                            {...register(`printed_fabrics.${index}.purchase_cd_percent`)}
                            className="h-6 mt-0.5 border-emerald-200 bg-emerald-50/50 text-emerald-700 text-[10px]"
                            placeholder="CD %"
                          />
                        </td>
                        <td className="py-1 px-2 bg-emerald-50">
                          <div 
                            data-testid={`purchase-amount-printed-${index}`}
                            className="h-8 flex items-center justify-center bg-emerald-100 rounded font-bold text-xs text-emerald-800"
                          >
                            {(watch(`printed_fabrics.${index}.purchase_amount`) || 0) > 0 ? watch(`printed_fabrics.${index}.purchase_amount`).toLocaleString() : '-'}
                          </div>
                        </td>
                        <td className="py-1 px-2 bg-blue-50">
                          <Input
                            data-testid={`sales-rate-printed-${index}`}
                            type="text"
                            inputMode="decimal"
                            {...register(`printed_fabrics.${index}.sales_rate`)}
                            className="h-8 border-blue-300 bg-blue-50 text-blue-800 text-xs"
                            placeholder="S.Rate"
                          />
                          <Input
                            data-testid={`sales-cd-printed-${index}`}
                            type="text"
                            inputMode="decimal"
                            {...register(`printed_fabrics.${index}.sales_cd_percent`)}
                            className="h-6 mt-0.5 border-blue-200 bg-blue-50/50 text-blue-700 text-[10px]"
                            placeholder="CD %"
                          />
                        </td>
                        <td className="py-1 px-2 bg-blue-50">
                          <div 
                            data-testid={`sales-amount-printed-${index}`}
                            className="h-8 flex items-center justify-center bg-blue-100 rounded font-bold text-xs text-blue-800"
                          >
                            {(watch(`printed_fabrics.${index}.sales_amount`) || 0) > 0 ? watch(`printed_fabrics.${index}.sales_amount`).toLocaleString() : '-'}
                          </div>
                        </td>
                        <td className="py-1 px-2">
                          <Select
                            value={currentStatus || 'Ready'}
                            onValueChange={(value) => setValue(`printed_fabrics.${index}.status`, value)}
                          >
                            <SelectTrigger data-testid={`status-${index}`} className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="Ready">Ready</SelectItem>
                              <SelectItem value="Forward">Forward</SelectItem>
                              <SelectItem value="No Stock">No Stock</SelectItem>
                              <SelectItem value="Delivered">Delivered</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-1 px-2">
                          {currentStatus === 'Delivered' ? (
                            <Input
                              data-testid={`bale-no-${index}`}
                              {...register(`printed_fabrics.${index}.bale_no`)}
                              className="h-8 border-maroon-200 text-xs"
                              placeholder="Bale#"
                            />
                          ) : (
                            <span className="text-xs text-maroon-500">-</span>
                          )}
                        </td>
                        <td className="py-1 px-1 text-center">
                          <Button
                            type="button"
                            data-testid={`remove-printed-${index}`}
                            onClick={() => removePrinted(index)}
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}

            {/* Mobile Card View - Conditional rendering */}
            {!isDesktop && (
            <div className="space-y-2">
              {printedFields.length === 0 && (
                <div className="py-6 text-center border border-dashed border-maroon-300 rounded-lg bg-maroon-50/50">
                  <div className="text-maroon-500 text-sm">No printed fabric items added yet.</div>
                  <div className="text-maroon-400 text-xs mt-1">Tap &quot;Add Row&quot; below to add fabric items.</div>
                </div>
              )}
              {printedFields.map((field, index) => {
                const currentStatus = watch(`printed_fabrics.${index}.status`);
                return (
                  <div 
                    key={field.id} 
                    className="border border-maroon-200 rounded-lg bg-white p-2 space-y-1.5"
                    ref={index === printedFields.length - 1 ? lastAddedPrintedRef : null}
                  >
                    {/* Header: Trade Name + Delete */}
                    <div className="flex items-center gap-2">
                      <MasterDataAutocomplete
                        value={watch(`printed_fabrics.${index}.trade_name`) || ''}
                        onValueChange={(value) => {
                          if (value === '__add_new__') {
                            handleOpenAddTradeItemDialog('Printed', index, 'printed');
                          } else {
                            setValue(`printed_fabrics.${index}.trade_name`, value);
                          }
                        }}
                        options={[
                          { value: '__add_new__', label: 'Add New Trade Item', pinned: true, testId: `add-trade-item-printed-mobile-${index}` },
                          ...masterPrintedTradeNames.map((tradeName) => ({
                            value: tradeName.name,
                            label: tradeName.name,
                            description: tradeName.supplier_name || '',
                            keywords: tradeName.supplier_name || '',
                          })),
                        ]}
                        placeholder="Select Trade Name..."
                        searchPlaceholder="Search trade items..."
                        emptyMessage="No matching printed trade item"
                        className="flex-1"
                        inputClassName="h-9 text-sm"
                        testId={`trade-name-printed-mobile-${index}`}
                        onFocus={() => { tradeItemDropdownFocusRef.current = true; }}
                        onBlur={() => { tradeItemDropdownFocusRef.current = false; }}
                      />
                      <Button
                        type="button"
                        data-testid={`remove-printed-mobile-${index}`}
                        onClick={() => removePrinted(index)}
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Row 1: Design No, Qty, Match */}
                    <div className="grid grid-cols-3 gap-1.5">
                      <Input
                        data-testid={`design-number-mobile-${index}`}
                        data-mobile-sequence="true"
                        {...register(`printed_fabrics.${index}.design_number`)}
                        className="h-8 text-sm"
                        placeholder="Design No."
                      />
                      <Input
                        data-testid={`quantity-mobile-${index}`}
                        data-mobile-sequence="true"
                        type="text"
                        inputMode="decimal"
                        {...register(`printed_fabrics.${index}.quantity`)}
                        className="h-8 text-sm"
                        placeholder="Qty"
                      />
                      <Input
                        data-testid={`num-matchings-mobile-${index}`}
                        data-mobile-sequence="true"
                        type="text"
                        inputMode="numeric"
                        {...register(`printed_fabrics.${index}.num_matchings`)}
                        className="h-8 text-sm"
                        placeholder="Match"
                      />
                    </div>

                    {/* Row 2: Unit, Total, Status */}
                    <div className="grid grid-cols-3 gap-1.5">
                      <div className="h-8 flex items-center justify-center bg-maroon-100 rounded text-xs font-medium text-maroon-700">
                        {watch(`printed_fabrics.${index}.si_unit`) || 'Unit'}
                      </div>
                      <div 
                        data-testid={`total-qty-mobile-${index}`}
                        className="h-8 flex items-center justify-center bg-maroon-200 rounded text-sm font-bold"
                      >
                        {watch(`printed_fabrics.${index}.total_qty`) || '—'}
                      </div>
                      <Select
                        value={currentStatus || 'Ready'}
                        onValueChange={(value) => setValue(`printed_fabrics.${index}.status`, value)}
                      >
                        <SelectTrigger data-testid={`status-mobile-${index}`} className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="Ready">Ready</SelectItem>
                          <SelectItem value="Forward">Forward</SelectItem>
                          <SelectItem value="No Stock">No Stock</SelectItem>
                          <SelectItem value="Delivered">Delivered</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Row 3: P.Rate, S.Rate, Bale */}
                    <div className="grid grid-cols-3 gap-1.5">
                      <Input
                        data-testid={`purchase-rate-printed-mobile-${index}`}
                        data-mobile-sequence="true"
                        type="text"
                        inputMode="decimal"
                        {...register(`printed_fabrics.${index}.purchase_rate`)}
                        className="h-8 text-sm bg-emerald-50 border-emerald-300 text-emerald-800"
                        placeholder="P.Rate"
                      />
                      <Input
                        data-testid={`sales-rate-printed-mobile-${index}`}
                        data-mobile-sequence="true"
                        data-mobile-append-button-testid={index === printedFields.length - 1 ? 'add-printed-fabric-button' : undefined}
                        data-mobile-next-testid={index === printedFields.length - 1 ? `design-number-mobile-${index + 1}` : undefined}
                        type="text"
                        inputMode="decimal"
                        {...register(`printed_fabrics.${index}.sales_rate`)}
                        className="h-8 text-sm bg-blue-50 border-blue-300 text-blue-800"
                        placeholder="S.Rate"
                      />
                      {currentStatus === 'Delivered' ? (
                        <Input
                          data-testid={`bale-no-mobile-${index}`}
                          data-mobile-sequence="true"
                          {...register(`printed_fabrics.${index}.bale_no`)}
                          className="h-8 text-sm"
                          placeholder="Bale#"
                        />
                      ) : (
                        <div className="h-8 flex items-center justify-center bg-gray-100 rounded text-xs text-gray-500">Bale</div>
                      )}
                    </div>

                    {/* Row 3b: P.CD%, S.CD% (optional) */}
                    <div className="grid grid-cols-3 gap-1.5">
                      <Input
                        data-testid={`purchase-cd-printed-mobile-${index}`}
                        type="text"
                        inputMode="decimal"
                        {...register(`printed_fabrics.${index}.purchase_cd_percent`)}
                        className="h-7 text-xs bg-emerald-50/50 border-emerald-200 text-emerald-700"
                        placeholder="P.CD %"
                      />
                      <Input
                        data-testid={`sales-cd-printed-mobile-${index}`}
                        type="text"
                        inputMode="decimal"
                        {...register(`printed_fabrics.${index}.sales_cd_percent`)}
                        className="h-7 text-xs bg-blue-50/50 border-blue-200 text-blue-700"
                        placeholder="S.CD %"
                      />
                      <div></div>
                    </div>

                    {/* Row 4: Amounts (read-only) */}
                    <div className="grid grid-cols-2 gap-1.5">
                      <div 
                        data-testid={`purchase-amount-printed-mobile-${index}`}
                        className="h-7 flex items-center justify-center bg-emerald-100 rounded text-xs font-bold text-emerald-800"
                      >
                        P: ₹{(watch(`printed_fabrics.${index}.purchase_amount`) || 0).toLocaleString()}
                      </div>
                      <div 
                        data-testid={`sales-amount-printed-mobile-${index}`}
                        className="h-7 flex items-center justify-center bg-blue-100 rounded text-xs font-bold text-blue-800"
                      >
                        S: ₹{(watch(`printed_fabrics.${index}.sales_amount`) || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            )}

            <div className="flex justify-end mt-3">
              <Button
                type="button"
                data-testid="add-printed-fabric-button"
                onClick={handleAddPrintedRow}
                size="sm"
                variant="outline"
                className="h-8 border-rose-600 text-maroon-700 hover:bg-maroon-50"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Row
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Dyed Fabrics Section */}
        <Card 
          className="border-maroon-200 border-l-4 border-l-rose-500"
          data-section="dyed"
          onFocus={() => setActiveSection('dyed')}
        >
          <CardHeader className="pb-3 p-4 lg:p-5">
            <CardTitle className="text-sm lg:text-lg font-semibold text-maroon-950">
              Dyed Fabrics
              <span className="ml-2 text-xs font-normal text-maroon-500">(Double Space to add)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 p-2 lg:p-5 lg:pt-0">
            <div className="space-y-4">
              {dyedFields.length === 0 && (
                <div className="py-8 text-center border border-dashed border-maroon-300 rounded-lg bg-maroon-50/50">
                  <div className="text-maroon-500 text-sm">
                    No dyed fabric items added yet.
                  </div>
                  <div className="text-maroon-400 text-xs mt-1">
                    Click &quot;Add Fabric&quot; and select a Trade Name. The ordering mode is set in Trade Name Master.
                  </div>
                </div>
              )}
              {dyedFields.map((field, dyedIndex) => {
                const selectedTradeName = watch(`dyed_fabrics.${dyedIndex}.trade_name`);
                const selectedTrade = masterDyedTradeNames.find(t => t.name === selectedTradeName);
                const itemMode = selectedTrade?.dyed_ordering_mode || watch(`dyed_fabrics.${dyedIndex}.mode`);
                
                // If no trade name selected yet, show placeholder
                if (!selectedTradeName) {
                  return (
                    <DyedFabricPlaceholder
                      key={field.id}
                      dyedIndex={dyedIndex}
                      watch={watch}
                      setValue={setValue}
                      removeDyed={removeDyed}
                      lastAddedDyedRef={dyedIndex === dyedFields.length - 1 ? lastAddedDyedRef : null}
                      masterTradeNames={masterDyedTradeNames}
                      masterSuppliers={masterSuppliers}
                      onOpenAddTradeItemDialog={handleOpenAddTradeItemDialog}
                      isDesktop={isDesktop}
                    />
                  );
                }
                
                // If trade name selected but mode is unassigned in master, show warning
                if (!itemMode || (itemMode !== 'fixed_chart' && itemMode !== 'pick_choose')) {
                  return (
                    <DyedFabricUnassigned
                      key={field.id}
                      dyedIndex={dyedIndex}
                      tradeName={selectedTradeName}
                      removeDyed={removeDyed}
                      lastAddedDyedRef={dyedIndex === dyedFields.length - 1 ? lastAddedDyedRef : null}
                    />
                  );
                }
                
                return itemMode === 'fixed_chart' ? (
                  <DyedFabricFixedChart
                    key={field.id}
                    dyedIndex={dyedIndex}
                    register={register}
                    control={control}
                    watch={watch}
                    setValue={setValue}
                    removeDyed={removeDyed}
                    masterTradeNames={masterDyedTradeNames}
                    isDesktop={isDesktop}
                    onOpenAddTradeItemDialog={handleOpenAddTradeItemDialog}
                    defaultMatching={selectedTrade?.default_matching}
                    lastAddedDyedRef={dyedIndex === dyedFields.length - 1 ? lastAddedDyedRef : null}
                  />
                ) : (
                  <DyedFabricItem
                    key={field.id}
                    dyedIndex={dyedIndex}
                    register={register}
                    control={control}
                    watch={watch}
                    setValue={setValue}
                    removeDyed={removeDyed}
                    masterTradeNames={masterDyedTradeNames}
                    isDesktop={isDesktop}
                    onOpenAddTradeItemDialog={handleOpenAddTradeItemDialog}
                    lastAddedDyedRef={dyedIndex === dyedFields.length - 1 ? lastAddedDyedRef : null}
                  />
                );
              })}
            </div>
            <div className="flex justify-end mt-3">
              <Button
                type="button"
                data-testid="add-dyed-fabric-button"
                onClick={handleAddDyedFabric}
                size="sm"
                variant="outline"
                className="h-8 border-rose-600 text-maroon-700 hover:bg-maroon-50"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Fabric
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div data-mobile-sticky-actions="true" className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 sticky sm:static bottom-0 bg-maroon-50 py-3 lg:py-4 px-2 lg:px-0 border-t border-maroon-200 -mx-2 lg:mx-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/orders')}
            className="border-maroon-300 hover:bg-maroon-100 order-2 sm:order-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            data-testid="save-order-button"
            disabled={loading}
            className="bg-maroon-900 hover:bg-maroon-800 order-1 sm:order-2"
          >
            {loading ? (
              'Saving...'
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {orderId ? 'Update Order' : 'Save Order'}
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Add Trade Item Dialog */}
      <AddTradeItemDialog
        open={addTradeItemDialogOpen}
        onOpenChange={setAddTradeItemDialogOpen}
        onItemCreated={handleTradeItemCreated}
        defaultFabricType={addTradeItemFabricType}
        suppliers={masterSuppliers}
      />
    </div>
  );
}

// Dyed Fabric Placeholder - Shows when no Trade Name is selected yet
function TradeNameAutocomplete({ value, onValueChange, tradeNames, onAddNew, testId, inputClassName, addTestId, className }) {
  return (
    <MasterDataAutocomplete
      value={value}
      onValueChange={(nextValue) => {
        if (nextValue === '__add_new__') onAddNew();
        else onValueChange(nextValue);
      }}
      options={[
        { value: '__add_new__', label: 'Add New Trade Item', description: 'Alt+C', pinned: true, testId: addTestId },
        ...tradeNames.map((tradeName) => ({
          value: tradeName.name,
          label: tradeName.name,
          description: [
            tradeName.supplier_name,
            tradeName.dyed_ordering_mode === 'fixed_chart' ? 'Fixed Chart' : '',
            tradeName.dyed_ordering_mode === 'pick_choose' ? 'Pick & Choose' : '',
          ].filter(Boolean).join(' · '),
          keywords: tradeName.supplier_name || '',
        })),
      ]}
      placeholder="Select Trade Name..."
      searchPlaceholder="Search trade items..."
      emptyMessage="No matching dyed trade item"
      className={className}
      inputClassName={inputClassName}
      testId={testId}
    />
  );
}

function DyedFabricPlaceholder({ dyedIndex, watch, setValue, removeDyed, masterTradeNames, masterSuppliers, onOpenAddTradeItemDialog, lastAddedDyedRef, isDesktop }) {
  return (
    <div className="border border-rose-200 rounded-lg bg-rose-50/30 p-3" ref={lastAddedDyedRef}>
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <TradeNameAutocomplete
            value={watch(`dyed_fabrics.${dyedIndex}.trade_name`) || ''}
            onValueChange={(value) => {
              setValue(`dyed_fabrics.${dyedIndex}.trade_name`, value);
              const selectedTrade = masterTradeNames.find(t => t.name === value);
              if (selectedTrade) {
                setValue(`dyed_fabrics.${dyedIndex}.mode`, selectedTrade.dyed_ordering_mode || null);
                setValue(`dyed_fabrics.${dyedIndex}.unit`, selectedTrade.si_unit || 'Metre');
                if (selectedTrade.dyed_ordering_mode === 'fixed_chart' && selectedTrade.default_matching) {
                  setValue(`dyed_fabrics.${dyedIndex}.matching`, selectedTrade.default_matching);
                }
                if (selectedTrade.dyed_ordering_mode === 'pick_choose') {
                  setValue(`dyed_fabrics.${dyedIndex}.colors`, [
                    { color_number: '', color_name: '', qty: '', unit: selectedTrade.si_unit || 'Metre', purchase_rate: '', sales_rate: '', purchase_amount: 0, sales_amount: 0 }
                  ]);
                }
                // Auto-fill default Purchase CD% from supplier master (only if not already set by user)
                const linkedSupplier = masterSuppliers?.find(s => s.id === selectedTrade.supplier_id);
                if (linkedSupplier && linkedSupplier.default_purchase_cd_percent) {
                  if (selectedTrade.dyed_ordering_mode === 'fixed_chart' && !watch(`dyed_fabrics.${dyedIndex}.purchase_cd_percent`)) {
                    setValue(`dyed_fabrics.${dyedIndex}.purchase_cd_percent`, linkedSupplier.default_purchase_cd_percent);
                  } else if (selectedTrade.dyed_ordering_mode === 'pick_choose' && !watch(`dyed_fabrics.${dyedIndex}.trade_purchase_cd_percent`)) {
                    setValue(`dyed_fabrics.${dyedIndex}.trade_purchase_cd_percent`, linkedSupplier.default_purchase_cd_percent);
                  }
                }
                if (!isDesktop) {
                  const nextTestId = selectedTrade.dyed_ordering_mode === 'fixed_chart'
                    ? `design-no-dyed-fixed-mobile-${dyedIndex}`
                    : `trade-purchase-rate-mobile-${dyedIndex}`;
                  focusMobileFieldByTestId(nextTestId);
                }
              }
            }}
            tradeNames={masterTradeNames}
            onAddNew={() => onOpenAddTradeItemDialog('Dyed', dyedIndex, 'dyed')}
            testId={`trade-name-dyed-placeholder-${dyedIndex}`}
            className="max-w-md"
            inputClassName="h-9"
            addTestId={`add-trade-item-dyed-placeholder-${dyedIndex}`}
          />
        </div>
        <Button
          type="button"
          data-testid={`remove-dyed-placeholder-${dyedIndex}`}
          onClick={() => removeDyed(dyedIndex)}
          variant="ghost"
          size="sm"
          className="h-9 w-9 p-0 text-red-600 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-xs text-maroon-500 mt-2">
        Select a Trade Name. Ordering mode (Fixed Chart / Pick & Choose) is configured in Trade Name Master.
      </p>
    </div>
  );
}

// Dyed Fabric Unassigned - Shows when Trade Name has no ordering mode set
function DyedFabricUnassigned({ dyedIndex, tradeName, removeDyed, lastAddedDyedRef }) {
  return (
    <div className="border border-red-300 rounded-lg bg-red-50 p-4" ref={lastAddedDyedRef}>
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-red-800">⚠️ Mode Not Set: {tradeName}</p>
          <p className="text-sm text-red-600 mt-1">
            This Trade Name does not have a Dyed Ordering Mode configured.
          </p>
          <p className="text-xs text-red-500 mt-2">
            Go to <strong>Master Data → Trade Names</strong> and set the Ordering Mode for this item.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => removeDyed(dyedIndex)}
          variant="outline"
          size="sm"
          className="border-red-300 text-red-700 hover:bg-red-100"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Remove
        </Button>
      </div>
    </div>
  );
}

// Dyed Fabric Fixed Chart - Single line entry like Printed Fabrics
function DyedFabricFixedChart({ dyedIndex, register, control, watch, setValue, removeDyed, masterTradeNames, isDesktop, onOpenAddTradeItemDialog, defaultMatching, lastAddedDyedRef }) {
  // Get SI unit for this trade name
  const selectedTradeName = watch(`dyed_fabrics.${dyedIndex}.trade_name`);
  const selectedTrade = masterTradeNames.find(t => t.name === selectedTradeName);
  let siUnit = selectedTrade?.si_unit || 'Metre';
  if (siUnit.toLowerCase() === 'metre') siUnit = 'MTRS';
  else if (siUnit.toLowerCase() === 'kgs') siUnit = 'KGS';
  else siUnit = siUnit.toUpperCase();

  // Auto-calculate total_qty = qty_per_matching × matching
  const qtyPerMatching = parseFloat(watch(`dyed_fabrics.${dyedIndex}.qty_per_matching`)) || 0;
  const matching = parseFloat(watch(`dyed_fabrics.${dyedIndex}.matching`)) || 0;
  const totalQty = qtyPerMatching * matching;
  
  // Auto-calculate amounts
  const purchaseRate = parseFloat(watch(`dyed_fabrics.${dyedIndex}.purchase_rate`)) || 0;
  const salesRate = parseFloat(watch(`dyed_fabrics.${dyedIndex}.sales_rate`)) || 0;
  const purchaseAmount = totalQty * purchaseRate;
  const salesAmount = totalQty * salesRate;
  
  const status = watch(`dyed_fabrics.${dyedIndex}.status`) || 'Ready';

  // Update calculated fields when inputs change
  useEffect(() => {
    setValue(`dyed_fabrics.${dyedIndex}.total_qty`, totalQty);
    setValue(`dyed_fabrics.${dyedIndex}.purchase_amount`, purchaseAmount);
    setValue(`dyed_fabrics.${dyedIndex}.sales_amount`, salesAmount);
    setValue(`dyed_fabrics.${dyedIndex}.unit`, siUnit);
  }, [totalQty, purchaseAmount, salesAmount, siUnit, setValue, dyedIndex]);

  // Desktop Table Row
  if (isDesktop) {
    return (
      <div className="border border-rose-200 rounded-md overflow-hidden bg-white" ref={lastAddedDyedRef}>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-rose-50/70 border-b border-rose-200">
              <th className="text-left py-2 px-2 text-xs font-semibold text-maroon-800 uppercase w-40">Trade Name</th>
              <th className="text-left py-2 px-2 text-xs font-semibold text-maroon-800 uppercase w-24">Design No.</th>
              <th className="text-left py-2 px-2 text-xs font-semibold text-maroon-800 uppercase w-20">Qty</th>
              <th className="text-center py-2 px-2 text-xs font-semibold text-maroon-800 uppercase w-16">Unit</th>
              <th className="text-left py-2 px-2 text-xs font-semibold text-maroon-800 uppercase w-16">Match</th>
              <th className="text-right py-2 px-2 text-xs font-semibold text-maroon-800 uppercase w-24">Total</th>
              <th className="text-left py-2 px-2 text-xs font-semibold text-emerald-700 uppercase bg-emerald-50 w-20">P.Rate</th>
              <th className="text-right py-2 px-2 text-xs font-semibold text-emerald-700 uppercase bg-emerald-50 w-24">P.Amt</th>
              <th className="text-left py-2 px-2 text-xs font-semibold text-blue-700 uppercase bg-blue-50 w-20">S.Rate</th>
              <th className="text-right py-2 px-2 text-xs font-semibold text-blue-700 uppercase bg-blue-50 w-24">S.Amt</th>
              <th className="text-left py-2 px-2 text-xs font-semibold text-maroon-800 uppercase w-24">Status</th>
              <th className="text-left py-2 px-2 text-xs font-semibold text-maroon-800 uppercase w-20">Bale</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-rose-100 hover:bg-rose-50/30">
              {/* Trade Name */}
              <td className="py-1 px-2">
                <TradeNameAutocomplete
                  value={watch(`dyed_fabrics.${dyedIndex}.trade_name`) || ''}
                  onValueChange={(value) => {
                    setValue(`dyed_fabrics.${dyedIndex}.trade_name`, value);
                  }}
                  tradeNames={masterTradeNames}
                  onAddNew={() => onOpenAddTradeItemDialog('Dyed', dyedIndex, 'dyed')}
                  testId={`trade-name-dyed-fixed-${dyedIndex}`}
                  inputClassName="h-8 text-xs"
                  addTestId={`add-trade-item-dyed-fixed-${dyedIndex}`}
                />
              </td>
              {/* Design Number */}
              <td className="py-1 px-2">
                <Input
                  data-testid={`design-no-dyed-fixed-${dyedIndex}`}
                  {...register(`dyed_fabrics.${dyedIndex}.design_number`)}
                  className="h-8 text-xs"
                  placeholder="Design"
                />
              </td>
              {/* Qty per Matching */}
              <td className="py-1 px-2">
                <Input
                  data-testid={`qty-dyed-fixed-${dyedIndex}`}
                  type="text"
                  inputMode="decimal"
                  {...register(`dyed_fabrics.${dyedIndex}.qty_per_matching`)}
                  className="h-8 text-xs w-16"
                  placeholder="Qty"
                />
              </td>
              {/* Unit (read-only) */}
              <td className="py-1 px-2 text-center">
                <div className="h-8 flex items-center justify-center bg-maroon-100 rounded text-xs font-bold text-maroon-700">
                  {siUnit}
                </div>
              </td>
              {/* Matching Count */}
              <td className="py-1 px-2">
                <Input
                  data-testid={`matching-dyed-fixed-${dyedIndex}`}
                  type="text"
                  inputMode="numeric"
                  {...register(`dyed_fabrics.${dyedIndex}.matching`)}
                  className="h-8 text-xs w-14"
                  placeholder="×"
                />
              </td>
              {/* Total Qty (calculated) */}
              <td className="py-1 px-2 text-right">
                <div className="h-8 flex items-center justify-end text-xs font-semibold text-maroon-800">
                  {totalQty > 0 ? `${totalQty.toFixed(2)} ${siUnit}` : '-'}
                </div>
              </td>
              {/* Purchase Rate */}
              <td className="py-1 px-2 bg-emerald-50">
                <Input
                  data-testid={`p-rate-dyed-fixed-${dyedIndex}`}
                  type="text"
                  inputMode="decimal"
                  {...register(`dyed_fabrics.${dyedIndex}.purchase_rate`)}
                  className="h-8 text-xs border-emerald-300 bg-emerald-50 w-16"
                  placeholder="₹"
                />
                <Input
                  data-testid={`p-cd-dyed-fixed-${dyedIndex}`}
                  type="text"
                  inputMode="decimal"
                  {...register(`dyed_fabrics.${dyedIndex}.purchase_cd_percent`)}
                  className="h-6 mt-0.5 text-[10px] border-emerald-200 bg-emerald-50/50 w-16"
                  placeholder="CD %"
                />
              </td>
              {/* Purchase Amount */}
              <td className="py-1 px-2 bg-emerald-50 text-right">
                <div className="h-8 flex items-center justify-end text-xs font-semibold text-emerald-700">
                  {purchaseAmount > 0 ? `₹${purchaseAmount.toLocaleString()}` : '-'}
                </div>
              </td>
              {/* Sales Rate */}
              <td className="py-1 px-2 bg-blue-50">
                <Input
                  data-testid={`s-rate-dyed-fixed-${dyedIndex}`}
                  type="text"
                  inputMode="decimal"
                  {...register(`dyed_fabrics.${dyedIndex}.sales_rate`)}
                  className="h-8 text-xs border-blue-300 bg-blue-50 w-16"
                  placeholder="₹"
                />
                <Input
                  data-testid={`s-cd-dyed-fixed-${dyedIndex}`}
                  type="text"
                  inputMode="decimal"
                  {...register(`dyed_fabrics.${dyedIndex}.sales_cd_percent`)}
                  className="h-6 mt-0.5 text-[10px] border-blue-200 bg-blue-50/50 w-16"
                  placeholder="CD %"
                />
              </td>
              {/* Sales Amount */}
              <td className="py-1 px-2 bg-blue-50 text-right">
                <div className="h-8 flex items-center justify-end text-xs font-semibold text-blue-700">
                  {salesAmount > 0 ? `₹${salesAmount.toLocaleString()}` : '-'}
                </div>
              </td>
              {/* Status */}
              <td className="py-1 px-2">
                <Select
                  value={status}
                  onValueChange={(value) => setValue(`dyed_fabrics.${dyedIndex}.status`, value)}
                >
                  <SelectTrigger data-testid={`status-dyed-fixed-${dyedIndex}`} className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="Ready">Ready</SelectItem>
                    <SelectItem value="Forward">Forward</SelectItem>
                    <SelectItem value="No Stock">No Stock</SelectItem>
                    <SelectItem value="Delivered">Delivered</SelectItem>
                  </SelectContent>
                </Select>
              </td>
              {/* Bale Number (only when Delivered) */}
              <td className="py-1 px-2">
                {status === 'Delivered' ? (
                  <Input
                    data-testid={`bale-dyed-fixed-${dyedIndex}`}
                    {...register(`dyed_fabrics.${dyedIndex}.bale_number`)}
                    className="h-8 text-xs w-16"
                    placeholder="Bale"
                  />
                ) : (
                  <div className="h-8 flex items-center justify-center text-xs text-maroon-400">-</div>
                )}
              </td>
              {/* Delete */}
              <td className="py-1 px-2 text-center">
                <Button
                  type="button"
                  data-testid={`remove-dyed-fixed-${dyedIndex}`}
                  onClick={() => removeDyed(dyedIndex)}
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Mobile Card View
  return (
    <div className="border border-rose-200 rounded-lg bg-white p-2 space-y-1.5" ref={lastAddedDyedRef}>
      {/* Header: Trade Name + Delete */}
      <div className="flex items-center gap-2">
        <TradeNameAutocomplete
          value={watch(`dyed_fabrics.${dyedIndex}.trade_name`) || ''}
          onValueChange={(value) => {
            setValue(`dyed_fabrics.${dyedIndex}.trade_name`, value);
          }}
          tradeNames={masterTradeNames}
          onAddNew={() => onOpenAddTradeItemDialog('Dyed', dyedIndex, 'dyed')}
          testId={`trade-name-dyed-fixed-mobile-${dyedIndex}`}
          className="flex-1"
          inputClassName="h-9 text-sm"
          addTestId={`add-trade-item-dyed-fixed-mobile-${dyedIndex}`}
        />
        <Button
          type="button"
          data-testid={`remove-dyed-fixed-mobile-${dyedIndex}`}
          onClick={() => removeDyed(dyedIndex)}
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 shrink-0"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Row 2: Design, Qty, Match (3 columns) */}
      <div className="grid grid-cols-3 gap-1.5">
        <Input
          data-testid={`design-no-dyed-fixed-mobile-${dyedIndex}`}
          data-mobile-sequence="true"
          {...register(`dyed_fabrics.${dyedIndex}.design_number`)}
          className="h-9 text-sm"
          placeholder="Design No."
        />
        <Input
          data-testid={`qty-dyed-fixed-mobile-${dyedIndex}`}
          data-mobile-sequence="true"
          type="text"
          inputMode="decimal"
          {...register(`dyed_fabrics.${dyedIndex}.qty_per_matching`)}
          className="h-9 text-sm"
          placeholder="Qty"
        />
        <Input
          data-testid={`matching-dyed-fixed-mobile-${dyedIndex}`}
          data-mobile-sequence="true"
          type="text"
          inputMode="numeric"
          {...register(`dyed_fabrics.${dyedIndex}.matching`)}
          className="h-9 text-sm"
          placeholder="Match ×"
        />
      </div>
      
      {/* Row 3: Unit, Total, Status (3 columns) */}
      <div className="grid grid-cols-3 gap-1.5">
        <div className="h-9 flex items-center justify-center bg-maroon-100 rounded text-sm font-bold text-maroon-700">
          {siUnit}
        </div>
        <div className="h-9 flex items-center justify-center bg-maroon-50 rounded text-sm font-semibold text-maroon-800">
          {totalQty > 0 ? totalQty.toFixed(2) : '-'}
        </div>
        <Select
          value={status}
          onValueChange={(value) => setValue(`dyed_fabrics.${dyedIndex}.status`, value)}
        >
          <SelectTrigger data-testid={`status-dyed-fixed-mobile-${dyedIndex}`} className="h-9 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="Ready">Ready</SelectItem>
            <SelectItem value="Forward">Forward</SelectItem>
            <SelectItem value="No Stock">No Stock</SelectItem>
            <SelectItem value="Delivered">Delivered</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Row 4: P.Rate, S.Rate, Bale (3 columns) */}
      <div className="grid grid-cols-3 gap-1.5">
        <Input
          data-testid={`p-rate-dyed-fixed-mobile-${dyedIndex}`}
          data-mobile-sequence="true"
          type="text"
          inputMode="decimal"
          {...register(`dyed_fabrics.${dyedIndex}.purchase_rate`)}
          className="h-9 text-sm border-emerald-300 bg-emerald-50"
          placeholder="P.Rate"
        />
        <Input
          data-testid={`s-rate-dyed-fixed-mobile-${dyedIndex}`}
          data-mobile-sequence="true"
          data-mobile-append-button-testid="add-dyed-fabric-button"
          data-mobile-next-testid={`trade-name-dyed-placeholder-${dyedIndex + 1}`}
          type="text"
          inputMode="decimal"
          {...register(`dyed_fabrics.${dyedIndex}.sales_rate`)}
          className="h-9 text-sm border-blue-300 bg-blue-50"
          placeholder="S.Rate"
        />
        {status === 'Delivered' ? (
          <Input
            data-testid={`bale-dyed-fixed-mobile-${dyedIndex}`}
            data-mobile-sequence="true"
            {...register(`dyed_fabrics.${dyedIndex}.bale_number`)}
            className="h-9 text-sm"
            placeholder="Bale No."
          />
        ) : (
          <div className="h-9 flex items-center justify-center text-sm text-maroon-400">-</div>
        )}
      </div>

      {/* Row 4b: P.CD%, S.CD% (optional) */}
      <div className="grid grid-cols-3 gap-1.5">
        <Input
          data-testid={`p-cd-dyed-fixed-mobile-${dyedIndex}`}
          type="text"
          inputMode="decimal"
          {...register(`dyed_fabrics.${dyedIndex}.purchase_cd_percent`)}
          className="h-7 text-xs border-emerald-200 bg-emerald-50/50 text-emerald-700"
          placeholder="P.CD %"
        />
        <Input
          data-testid={`s-cd-dyed-fixed-mobile-${dyedIndex}`}
          type="text"
          inputMode="decimal"
          {...register(`dyed_fabrics.${dyedIndex}.sales_cd_percent`)}
          className="h-7 text-xs border-blue-200 bg-blue-50/50 text-blue-700"
          placeholder="S.CD %"
        />
        <div></div>
      </div>
      
      {/* Row 5: Amounts (2 columns) */}
      <div className="grid grid-cols-2 gap-1.5">
        <div className="h-8 flex items-center justify-center bg-emerald-100 rounded text-sm font-semibold text-emerald-700">
          P: ₹{purchaseAmount > 0 ? purchaseAmount.toLocaleString() : '0'}
        </div>
        <div className="h-8 flex items-center justify-center bg-blue-100 rounded text-sm font-semibold text-blue-700">
          S: ₹{salesAmount > 0 ? salesAmount.toLocaleString() : '0'}
        </div>
      </div>
    </div>
  );
}

// Dyed Fabric Item with nested colors - Responsive layout (Pick & Choose mode)
function DyedFabricItem({ dyedIndex, register, control, watch, setValue, removeDyed, masterTradeNames, isDesktop, onOpenAddTradeItemDialog, lastAddedDyedRef }) {
  const { fields: colorFields, append: appendColor, remove: removeColor } = useFieldArray({
    control,
    name: `dyed_fabrics.${dyedIndex}.colors`,
  });

  // State to track if Color Name column is visible
  const [showColorName, setShowColorName] = useState(false);

  // Get SI unit for this trade name to show in header (with abbreviation)
  const selectedTradeName = watch(`dyed_fabrics.${dyedIndex}.trade_name`);
  const selectedTrade = masterTradeNames.find(t => t.name === selectedTradeName);
  let siUnit = selectedTrade?.si_unit || 'Metre';
  // Convert to abbreviations: Metre -> MTRS, Kgs -> KGS
  if (siUnit.toLowerCase() === 'metre') siUnit = 'MTRS';
  else if (siUnit.toLowerCase() === 'kgs') siUnit = 'KGS';
  else siUnit = siUnit.toUpperCase();

  // Get trade rates for display in color rows
  const tradePurchaseRate = watch(`dyed_fabrics.${dyedIndex}.trade_purchase_rate`) || 0;
  const tradeSalesRate = watch(`dyed_fabrics.${dyedIndex}.trade_sales_rate`) || 0;

  return (
    <div className="border border-maroon-200 rounded-md overflow-hidden" ref={lastAddedDyedRef}>
      {/* Desktop Header - Hidden on mobile */}
      <div className="hidden md:flex items-center justify-between bg-maroon-50 px-4 py-2 border-b border-maroon-200 flex-wrap gap-2">
        <div className="flex-1 flex items-center gap-3 flex-wrap">
          <Label className="text-xs font-medium text-maroon-700 uppercase whitespace-nowrap">Trade Name:</Label>
          <TradeNameAutocomplete
            value={watch(`dyed_fabrics.${dyedIndex}.trade_name`) || ''}
            onValueChange={(value) => {
              setValue(`dyed_fabrics.${dyedIndex}.trade_name`, value);
            }}
            tradeNames={masterTradeNames}
            onAddNew={() => onOpenAddTradeItemDialog('Dyed', dyedIndex, 'dyed')}
            testId={`trade-name-dyed-${dyedIndex}`}
            className="w-48"
            inputClassName="h-8 text-xs"
            addTestId={`add-trade-item-dyed-${dyedIndex}`}
          />
          
          {/* Purchase Rate */}
          <Label className="text-xs font-medium text-emerald-700 uppercase whitespace-nowrap ml-2">P.Rate (₹):</Label>
          <Input
            data-testid={`trade-purchase-rate-${dyedIndex}`}
            type="text"
            inputMode="decimal"
            {...register(`dyed_fabrics.${dyedIndex}.trade_purchase_rate`)}
            className="h-8 w-24 border-emerald-300 bg-emerald-50 text-emerald-800"
            placeholder="P.Rate"
          />

          {/* Purchase CD% */}
          <Label className="text-xs font-medium text-emerald-700 uppercase whitespace-nowrap ml-1">P.CD%:</Label>
          <Input
            data-testid={`trade-purchase-cd-${dyedIndex}`}
            type="text"
            inputMode="decimal"
            {...register(`dyed_fabrics.${dyedIndex}.trade_purchase_cd_percent`)}
            className="h-8 w-16 border-emerald-200 bg-emerald-50/50 text-emerald-700"
            placeholder="CD %"
          />
          
          {/* Sales Rate */}
          <Label className="text-xs font-medium text-blue-700 uppercase whitespace-nowrap ml-2">S.Rate (₹):</Label>
          <Input
            data-testid={`trade-sales-rate-${dyedIndex}`}
            type="text"
            inputMode="decimal"
            {...register(`dyed_fabrics.${dyedIndex}.trade_sales_rate`)}
            className="h-8 w-24 border-blue-300 bg-blue-50 text-blue-800"
            placeholder="S.Rate"
          />

          {/* Sales CD% */}
          <Label className="text-xs font-medium text-blue-700 uppercase whitespace-nowrap ml-1">S.CD%:</Label>
          <Input
            data-testid={`trade-sales-cd-${dyedIndex}`}
            type="text"
            inputMode="decimal"
            {...register(`dyed_fabrics.${dyedIndex}.trade_sales_cd_percent`)}
            className="h-8 w-16 border-blue-200 bg-blue-50/50 text-blue-700"
            placeholder="CD %"
          />
          
          {/* SI Unit Display */}
          {selectedTradeName && (
            <span className="ml-2 text-xs font-medium bg-maroon-200 px-2 py-1 rounded text-maroon-700">
              Unit: {siUnit}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            data-testid={`remove-dyed-${dyedIndex}`}
            onClick={() => removeDyed(dyedIndex)}
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Mobile Header - Conditional rendering */}
      {!isDesktop && (
      <div className="bg-maroon-50 p-2 border-b border-maroon-200 space-y-1.5">
        <div className="flex items-center gap-2">
          <TradeNameAutocomplete
            value={watch(`dyed_fabrics.${dyedIndex}.trade_name`) || ''}
            onValueChange={(value) => {
              setValue(`dyed_fabrics.${dyedIndex}.trade_name`, value);
            }}
            tradeNames={masterTradeNames}
            onAddNew={() => onOpenAddTradeItemDialog('Dyed', dyedIndex, 'dyed')}
            testId={`trade-name-dyed-mobile-${dyedIndex}`}
            className="flex-1"
            inputClassName="h-9 text-sm"
            addTestId={`add-trade-item-dyed-mobile-${dyedIndex}`}
          />
          <Button
            type="button"
            data-testid={`remove-dyed-mobile-${dyedIndex}`}
            onClick={() => removeDyed(dyedIndex)}
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 shrink-0"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Rates Row + Unit for Mobile */}
        <div className="grid grid-cols-3 gap-1.5">
          <Input
            data-testid={`trade-purchase-rate-mobile-${dyedIndex}`}
            data-mobile-sequence="true"
            type="text"
            inputMode="decimal"
            {...register(`dyed_fabrics.${dyedIndex}.trade_purchase_rate`)}
            className="h-8 text-sm bg-emerald-50 border-emerald-300 text-emerald-800"
            placeholder="P.Rate"
          />
          <Input
            data-testid={`trade-sales-rate-mobile-${dyedIndex}`}
            data-mobile-sequence="true"
            type="text"
            inputMode="decimal"
            {...register(`dyed_fabrics.${dyedIndex}.trade_sales_rate`)}
            className="h-8 text-sm bg-blue-50 border-blue-300 text-blue-800"
            placeholder="S.Rate"
          />
          {selectedTradeName && (
            <div className="h-8 flex items-center justify-center bg-maroon-200 rounded text-xs font-medium text-maroon-700">
              {siUnit}
            </div>
          )}
        </div>

        {/* CD% Row for Mobile (optional) */}
        <div className="grid grid-cols-3 gap-1.5">
          <Input
            data-testid={`trade-purchase-cd-mobile-${dyedIndex}`}
            type="text"
            inputMode="decimal"
            {...register(`dyed_fabrics.${dyedIndex}.trade_purchase_cd_percent`)}
            className="h-7 text-xs bg-emerald-50/50 border-emerald-200 text-emerald-700"
            placeholder="P.CD %"
          />
          <Input
            data-testid={`trade-sales-cd-mobile-${dyedIndex}`}
            type="text"
            inputMode="decimal"
            {...register(`dyed_fabrics.${dyedIndex}.trade_sales_cd_percent`)}
            className="h-7 text-xs bg-blue-50/50 border-blue-200 text-blue-700"
            placeholder="S.CD %"
          />
          <div></div>
        </div>
      </div>
      )}

      {/* Desktop Table View - Conditional rendering */}
      {isDesktop && (
      <div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-maroon-50/50 border-b border-maroon-200">
              <th className="text-left py-2 px-3 text-xs font-semibold text-maroon-800 uppercase">
                <div className="flex items-center gap-1">
                  Color No.
                  {!showColorName && (
                    <button
                      type="button"
                      onClick={() => setShowColorName(true)}
                      className="ml-1 text-yellow-600 font-bold text-sm hover:text-yellow-700"
                      title="Add Color Name column"
                    >
                      +
                    </button>
                  )}
                </div>
              </th>
              {showColorName && (
                <th className="text-left py-2 px-3 text-xs font-semibold text-maroon-800 uppercase">
                  <div className="flex items-center gap-1">
                    Color Name
                    <button
                      type="button"
                      onClick={() => setShowColorName(false)}
                      className="ml-1 text-red-500 font-bold text-xs hover:text-red-700"
                      title="Remove Color Name column"
                    >
                      ×
                    </button>
                  </div>
                </th>
              )}
              <th className="text-left py-2 px-3 text-xs font-semibold text-maroon-800 uppercase">Quantity</th>
              <th className="text-left py-2 px-3 text-xs font-semibold text-maroon-800 uppercase bg-maroon-100">Unit</th>
              <th className="text-left py-2 px-3 text-xs font-semibold text-emerald-700 uppercase bg-emerald-50">P.Rate (₹)</th>
              <th className="text-left py-2 px-3 text-xs font-semibold text-emerald-700 uppercase bg-emerald-50">P.Amt (₹)</th>
              <th className="text-left py-2 px-3 text-xs font-semibold text-blue-700 uppercase bg-blue-50">S.Rate (₹)</th>
              <th className="text-left py-2 px-3 text-xs font-semibold text-blue-700 uppercase bg-blue-50">S.Amt (₹)</th>
              <th className="text-center py-2 px-3 text-xs font-semibold text-maroon-800 uppercase w-16"></th>
            </tr>
          </thead>
          <tbody>
            {colorFields.map((colorField, colorIndex) => (
              <tr key={colorField.id} className="border-b border-maroon-100 hover:bg-maroon-50/50">
                <td className="py-1.5 px-3">
                  <Input
                    data-testid={`color-number-${dyedIndex}-${colorIndex}`}
                    type="text"
                    inputMode="numeric"
                    {...register(`dyed_fabrics.${dyedIndex}.colors.${colorIndex}.color_number`)}
                    className="h-8 border-maroon-200 w-20"
                    placeholder="No."
                  />
                </td>
                {showColorName && (
                  <td className="py-1.5 px-3">
                    <Input
                      data-testid={`color-name-${dyedIndex}-${colorIndex}`}
                      {...register(`dyed_fabrics.${dyedIndex}.colors.${colorIndex}.color_name`)}
                      className="h-8 border-maroon-200 w-28"
                      placeholder="Color name"
                    />
                  </td>
                )}
                <td className="py-1.5 px-3">
                  <Input
                    data-testid={`color-qty-${dyedIndex}-${colorIndex}`}
                    type="text"
                    inputMode="decimal"
                    {...register(`dyed_fabrics.${dyedIndex}.colors.${colorIndex}.qty`)}
                    className="h-8 border-maroon-200 w-24"
                    placeholder="Qty"
                  />
                </td>
                <td className="py-1.5 px-3 bg-maroon-100">
                  <div
                    data-testid={`color-unit-${dyedIndex}-${colorIndex}`}
                    className="h-8 flex items-center justify-center bg-maroon-200 rounded font-bold text-xs text-maroon-800"
                  >
                    {watch(`dyed_fabrics.${dyedIndex}.colors.${colorIndex}.unit`) || siUnit}
                  </div>
                </td>
                <td className="py-1.5 px-3 bg-emerald-50">
                  <div
                    data-testid={`color-purchase-rate-${dyedIndex}-${colorIndex}`}
                    className="h-8 flex items-center justify-center bg-emerald-100 rounded font-semibold text-xs text-emerald-800"
                  >
                    {tradePurchaseRate > 0 ? tradePurchaseRate.toLocaleString() : '-'}
                  </div>
                </td>
                <td className="py-1.5 px-3 bg-emerald-50">
                  <div
                    data-testid={`color-purchase-amount-${dyedIndex}-${colorIndex}`}
                    className="h-8 flex items-center justify-center bg-emerald-100 rounded font-bold text-xs text-emerald-800"
                  >
                    {(watch(`dyed_fabrics.${dyedIndex}.colors.${colorIndex}.purchase_amount`) || 0) > 0 ? watch(`dyed_fabrics.${dyedIndex}.colors.${colorIndex}.purchase_amount`).toLocaleString() : '-'}
                  </div>
                </td>
                <td className="py-1.5 px-3 bg-blue-50">
                  <div
                    data-testid={`color-sales-rate-${dyedIndex}-${colorIndex}`}
                    className="h-8 flex items-center justify-center bg-blue-100 rounded font-semibold text-xs text-blue-800"
                  >
                    {tradeSalesRate > 0 ? tradeSalesRate.toLocaleString() : '-'}
                  </div>
                </td>
                <td className="py-1.5 px-3 bg-blue-50">
                  <div
                    data-testid={`color-sales-amount-${dyedIndex}-${colorIndex}`}
                    className="h-8 flex items-center justify-center bg-blue-100 rounded font-bold text-xs text-blue-800"
                  >
                    {(watch(`dyed_fabrics.${dyedIndex}.colors.${colorIndex}.sales_amount`) || 0) > 0 ? watch(`dyed_fabrics.${dyedIndex}.colors.${colorIndex}.sales_amount`).toLocaleString() : '-'}
                  </div>
                </td>
                <td className="py-1.5 px-3 text-center">
                  <Button
                    type="button"
                    data-testid={`remove-color-${dyedIndex}-${colorIndex}`}
                    onClick={() => removeColor(colorIndex)}
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                    disabled={colorFields.length === 1}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Mobile Card View for Colors - Conditional rendering */}
      {!isDesktop && (
      <div className="p-2 space-y-2">
        {/* Toggle Color Name for mobile */}
        <div className="flex justify-end">
          {!showColorName ? (
            <button
              type="button"
              data-testid={`show-color-name-mobile-${dyedIndex}`}
              onClick={() => setShowColorName(true)}
              className="text-xs text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded hover:bg-yellow-200"
            >
              + Color Name
            </button>
          ) : (
            <button
              type="button"
              data-testid={`hide-color-name-mobile-${dyedIndex}`}
              onClick={() => setShowColorName(false)}
              className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded hover:bg-red-100"
            >
              × Hide Name
            </button>
          )}
        </div>

        {colorFields.map((colorField, colorIndex) => (
          <div key={colorField.id} className="border border-maroon-100 rounded-lg bg-white p-2 space-y-1.5">
            {/* Row 1: Color No, Color Name (if visible), Qty, Delete */}
            <div className="flex items-center gap-1.5">
              <Input
                data-testid={`color-number-mobile-${dyedIndex}-${colorIndex}`}
                data-mobile-sequence="true"
                type="text"
                inputMode="numeric"
                {...register(`dyed_fabrics.${dyedIndex}.colors.${colorIndex}.color_number`)}
                className="h-8 text-sm w-16"
                placeholder="Color#"
              />
              {showColorName && (
                <Input
                  data-testid={`color-name-mobile-${dyedIndex}-${colorIndex}`}
                  data-mobile-sequence="true"
                  {...register(`dyed_fabrics.${dyedIndex}.colors.${colorIndex}.color_name`)}
                  className="h-8 text-sm flex-1"
                  placeholder="Color Name"
                />
              )}
              <Input
                data-testid={`color-qty-mobile-${dyedIndex}-${colorIndex}`}
                data-mobile-sequence="true"
                data-mobile-append-button-testid={colorIndex === colorFields.length - 1 ? `add-color-${dyedIndex}` : undefined}
                data-mobile-append-priority={colorIndex === colorFields.length - 1 ? 'true' : undefined}
                data-mobile-next-testid={colorIndex === colorFields.length - 1 ? `color-number-mobile-${dyedIndex}-${colorIndex + 1}` : undefined}
                type="text"
                inputMode="decimal"
                {...register(`dyed_fabrics.${dyedIndex}.colors.${colorIndex}.qty`)}
                className="h-8 text-sm w-20"
                placeholder="Qty"
              />
              <Button
                type="button"
                data-testid={`remove-color-mobile-${dyedIndex}-${colorIndex}`}
                onClick={() => removeColor(colorIndex)}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 shrink-0"
                disabled={colorFields.length === 1}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>

            {/* Row 2: Amounts (read-only) */}
            <div className="grid grid-cols-2 gap-1.5">
              <div 
                data-testid={`color-purchase-amount-mobile-${dyedIndex}-${colorIndex}`}
                className="h-7 flex items-center justify-center bg-emerald-100 rounded text-xs font-bold text-emerald-800"
              >
                P: ₹{(watch(`dyed_fabrics.${dyedIndex}.colors.${colorIndex}.purchase_amount`) || 0).toLocaleString()}
              </div>
              <div 
                data-testid={`color-sales-amount-mobile-${dyedIndex}-${colorIndex}`}
                className="h-7 flex items-center justify-center bg-blue-100 rounded text-xs font-bold text-blue-800"
              >
                S: ₹{(watch(`dyed_fabrics.${dyedIndex}.colors.${colorIndex}.sales_amount`) || 0).toLocaleString()}
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Add Color Button */}
      <div className="flex justify-end mt-2 px-3 pb-3">
        <Button
          type="button"
          data-testid={`add-color-${dyedIndex}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            const tradePurchaseRate = watch(`dyed_fabrics.${dyedIndex}.trade_purchase_rate`) || '';
            const tradeSalesRate = watch(`dyed_fabrics.${dyedIndex}.trade_sales_rate`) || '';
            appendColor({ 
              color_number: '', 
              color_name: '',
              qty: '', 
              unit: siUnit, 
              purchase_rate: tradePurchaseRate, 
              sales_rate: tradeSalesRate, 
              purchase_amount: 0, 
              sales_amount: 0 
            });
            if (!isDesktop) {
              focusMobileFieldByTestId(`color-number-mobile-${dyedIndex}-${colorFields.length}`);
            }
          }}
          size="sm"
          variant="outline"
          className="h-7 text-xs border-maroon-400 text-maroon-600 hover:bg-maroon-50"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Color
        </Button>
      </div>
    </div>
  );
}
